import dayjs from "dayjs";
import { create } from "zustand";
import {
  getColleges,
  getSubPools,
  getCounties,
  getEmployment,
  getEmploymentTypes,
  getOcafFacilities,
  getEmploymentJobTitles,
  getAgeGroups,
  getSurveyData,
} from "@/services/employment";
import { getUserEmploymentById } from "@/services/user";
import { useAppStore } from "@/stores/appStore";

const useStepperStore = create((set) => ({
  activeStep: 0,
  setActiveStep: (step) => set(() => ({ activeStep: step })),
  allowNavigation: false,
  setAllowNavigation: (allow) => set(() => ({ allowNavigation: allow })),
  successMessage: "",
  setSuccessMessage: (message) => set(() => ({ successMessage: message })),
  selectedCard: null,
  setSelectedCard: (card) => set(() => ({ selectedCard: card })),
}));

const useEmploymentStore = create((set) => ({
  loading: false,
  error: "",
  isNewEmployment: false,
  originalId: null,
  employment: {
    id: "",
    type: "",
    agency: "",
    employerName: "",
    agencyCCRS: "",
    college: "",
    departmentChair: "",
    schoolName: "",
    county: "",
    licenseNumber: "",
    parentAgencyId: "",
    default: false,
    roles: [],
  },
  isPrimary: false,
  subConsent: false,
  surveyData: null,
  surveyDataLoading: false,
  setIsPrimary: (isPrimary) =>
    set((state) => ({
      ...state,
      isPrimary: isPrimary,
      employment: {
        ...state.employment,
        default: isPrimary ? 1 : 0,
      },
    })),
  setisSubConsent: (isSubConsent) =>
      set((state) => ({
        ...state,
        isSubConsent: isSubConsent,
        employment: {
          ...state.employment,
          subConsent: isSubConsent,
        },
      })),

  setIsNewEmployment: (isNew, originalId) =>
    set(() => ({ isNewEmployment: isNew, originalId })),
  setEmployment: (updatedEmployment) => {
    set((state) => ({
      employment: { ...state.employment, ...updatedEmployment },
    }));
  },
  setRoles: (updatedRoles) => {
    set((state) => ({
      employment: {
        ...state.employment,
        roles: [...updatedRoles],
      },
    }));
  },
  fetchEmployment: async (employmentId) => {
    set((state) => ({ ...state, loading: true }));
    try {
      const employment = await getUserEmploymentById(employmentId);
      // Get the employmentTypes state and fetchEmploymentTypes action from useEmploymentTypeStore
      const { fetchEmploymentTypes } = useEmploymentTypeStore.getState();
      await fetchEmploymentTypes();
      // State needs to be fetched again after the fetchEmploymentTypes action is called
      const { employmentTypes } = useEmploymentTypeStore.getState();
      const employmentType = employmentTypes.find(
        (type) => type.id === employment.employment_type
      );
      // Get the counties state and fetchCounties action from useCountiesStore
      const { fetchCounties } = useCountiesStore.getState();
      await fetchCounties();
      // State needs to be fetched again after the fetchCounties action is called
      const { counties } = useCountiesStore.getState();
      const county = counties.find(
        (county) => county.id === employment.employer_county
      );
      const { fetchAgeGroups } = useAgeGroupStore.getState();
      await fetchAgeGroups();
      const { ageGroups, getAgeGroupName } = useAgeGroupStore.getState();

      const { fetchEmploymentJobTitles } =
        useEmploymentJobTitleStore.getState();
      await fetchEmploymentJobTitles();
      const { employmentJobTitles } = useEmploymentJobTitleStore.getState();
      // Map the roles into the format required by the employment object:
      const roles = employment.roles.map((role) => {
        const rawAgeGroups = role.age_groups.split(",");
        const mappedAgeGroup = {};
        ageGroups.forEach((ageGroup) => {
          mappedAgeGroup[getAgeGroupName(ageGroup.id)] = rawAgeGroups.includes(
            ageGroup.id
          );
        });
        const currentJobTitle = employmentJobTitles.find(
          (job) => job.id === role.job_title_id
        );
        return {
          id: role.id,
          role: currentJobTitle,
          facilityName: role.facilityName,
          dcyCertificationType: role.dcyCertificationType,
          startDate: dayjs(role.start_date),
          endDate:
            role.end_date === "11/30/-0001" ? null : dayjs(role.end_date),
          default: role.default,
          hours: role.hr_per_week === "-1" ? "" : role.hr_per_week,
          months: role.months_per_year === "-1" ? "" : role.months_per_year,
          pay: role.hourly_pay === "-1.00" ? "" : role.hourly_pay,
          employed: role.end_date === "11/30/-0001",
          ageGroups: mappedAgeGroup,
        };
      });

      const parentAgencyId = employment.agency_id ?? "";

      // `consented` is true/false for an Employment Agency (e.g. Sub Pool)
      // employer, or null when this employment isn't agency-sourced at all
      // (see User::get_employment_row()) - coerce both falsy cases to false.
      const subConsent = !!employment.consented;

      const mappedEmployment = {
        id: employment.employment_type,
        type: employmentType.type,
        schoolName: employment.school_district,
        employerName: employment.employer_name,
        licenseNumber: employment.license_number,
        county,
        roles,
        parentAgencyId,
        default: employment.default,
        subConsent,
      };
      set((state) => ({
        ...state,
        error: "",
        employment: mappedEmployment,
        isSubConsent: subConsent,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  isEmploymentSubmitting: false,
  setIsEmploymentSubmitting: (isSubmitting) =>
    set((state) => ({ ...state, isEmploymentSubmitting: isSubmitting })),
  fetchSurveyData: async (surveyId, oin) => {
    set((state) => ({ ...state, surveyDataLoading: true }));
    try {
      const data = await getSurveyData(surveyId, oin);
      set((state) => ({ ...state, error: "", surveyData: data }));
    } catch (error) {
      set((state) => ({ ...state, error: error.message }));
    } finally {
      set((state) => ({ ...state, surveyDataLoading: false }));
    }
  },
}));

const useEmploymentRecordsStore = create((set) => ({
  init: false,
  employmentRecords: [],
  loading: false,
  error: "",
  managedEmploymentRecords: [],
  setError: (error) => set(() => ({ error })),
  fetchEmploymentRecords: async (forceRefetch = false) => {
    const { employmentRecords, loading } = useEmploymentRecordsStore.getState();
    const { user } = useAppStore.getState();
    if (!forceRefetch && (employmentRecords.length > 0 || loading)) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      const employmentRecords = await getEmployment(user.user_id);
      set((state) => ({ ...state, error: "", employmentRecords }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "There was an error fetching your employment records. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
        init: true,
      }));
    }
  },
  clearEmploymentRecords: () => {
    set((state) => ({ ...state, employmentRecords: [] }));
  },
  fetchManagedEmploymentRecords: async (
    managedUserId,
    forceRefetch = false
  ) => {
    const { managedEmploymentRecords, loading } =
      useEmploymentRecordsStore.getState();
    if (!forceRefetch && (managedEmploymentRecords.length > 0 || loading)) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      const managedEmploymentRecords = await getEmployment(managedUserId);
      set((state) => ({ ...state, error: "", managedEmploymentRecords }));
    } catch (error) {
      set((state) => ({
        ...state,
        error:
          "There was an error fetching your employment records. Please try again or contact support.",
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
        init: true,
      }));
    }
  },
  clearManagedEmploymentRecords: () => {
    set((state) => ({ ...state, managedEmploymentRecords: [] }));
  },
}));

const useCountiesStore = create((set) => ({
  counties: [],
  loading: false,
  error: "",

  fetchCounties: async () => {
    const { counties } = useCountiesStore.getState();
    if (counties.length > 0) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let counties = await getCounties();
      counties = counties.map((c) => {
        return { label: c.county, id: c.id };
      });
      set((state) => ({ ...state, error: "", counties }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

const useCollegesStore = create((set) => ({
  colleges: [],
  loading: false,
  error: "",

  fetchColleges: async () => {
    const { colleges } = useCollegesStore.getState();
    if (colleges.length > 0) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let colleges = await getColleges();
      colleges = colleges.map((c) => {
        return { label: c.school_name, id: c.id };
      });
      set((state) => ({ ...state, error: "", colleges }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

const useSubPoolsStore = create((set) => ({
  subPools: [],
  loading: false,
  error: "",

  fetchSubPools: async () => {
    const { subPools } = useSubPoolsStore.getState();
    if (subPools.length > 0) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let subPools = await getSubPools();
      subPools = subPools.map((c) => {
        return {
          label: c.sub_agency_name,
          id: c.id,
          licenseNumber: c.centers_license_number,
        };
      });
      set((state) => ({ ...state, error: "", subPools }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

const useAgeGroupStore = create((set) => ({
  ageGroups: [],
  loading: false,
  error: "",
  getAgeGroupName: (id) => {
    switch (id) {
      case "1":
        return "infant";
      case "2":
        return "toddler";
      case "3":
        return "preschool";
      case "4":
        return "schoolAge";
      case "5":
        return "mixedAge";
      case "6":
        return "administrator";
      default:
        return "";
    }
  },
  getAgeGroupById: (name) => {
    switch (name) {
      case "infant":
        return "1";
      case "toddler":
        return "2";
      case "preschool":
        return "3";
      case "schoolAge":
        return "4";
      case "mixedAge":
        return "5";
      case "administrator":
        return "6";
      default:
        return "";
    }
  },
  fetchAgeGroups: async () => {
    const { ageGroups } = useAgeGroupStore.getState();
    if (ageGroups.length > 0) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      let ageGroups = await getAgeGroups();
      ageGroups = ageGroups.map((a) => {
        return { label: a.value, id: a.id };
      });
      set((state) => ({ ...state, error: "", ageGroups }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

const useEmploymentTypeStore = create((set) => ({
  employmentTypes: [],
  loading: false,
  error: "",
  filteredEmploymentTypes: [],
  setFilteredEmploymentTypes: (filteredEmploymentTypes) => {
    set((state) => ({ ...state, filteredEmploymentTypes }));
  },
  fetchEmploymentTypes: async () => {
    const { employmentTypes } = useEmploymentTypeStore.getState();
    if (employmentTypes.length > 0) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      const fetchedEmploymentTypes = await getEmploymentTypes();
      set((state) => ({
        ...state,
        error: "",
        employmentTypes: fetchedEmploymentTypes,
        filteredEmploymentTypes: fetchedEmploymentTypes,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

const useEmploymentJobTitleStore = create((set) => ({
  employmentJobTitles: [],
  loading: false,
  error: "",
  filteredEmploymentJobTitles: [],
  ocafFacilities: [],
  fetchOCAFFacilties: async (agencyId) => {
    set((state) => ({ ...state, loading: true }));
    try {
      const facilities = await getOcafFacilities(agencyId);
      set((state) => ({ ...state, error: "", ocafFacilities: facilities }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  setFilteredEmploymentJobTitles: (employmentTypeId) => {
    set((state) => ({
      ...state,
      filteredEmploymentJobTitles: state.employmentJobTitles.filter(
        (jobTitle) => jobTitle.employmentTypeId === employmentTypeId
      ),
    }));
  },
  fetchEmploymentJobTitles: async () => {
    const { employmentJobTitles } = useEmploymentJobTitleStore.getState();
    if (employmentJobTitles.length > 0) {
      return;
    }
    set((state) => ({ ...state, loading: true }));
    try {
      const jobTitles = await getEmploymentJobTitles();
      const mappedJobTitles = jobTitles.map((jobTitle) => ({
        label: jobTitle.title,
        id: jobTitle.id,
        employmentTypeId: jobTitle.type_id,
        ageGroupOptions: jobTitle.age_group_options || "",
      }));
      set((state) => ({
        ...state,
        error: "",
        employmentJobTitles: mappedJobTitles,
        filteredJobTitles: mappedJobTitles,
      }));
    } catch (error) {
      set((state) => ({
        ...state,
        error: error.message,
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));

export {
  useEmploymentTypeStore,
  useEmploymentRecordsStore,
  useEmploymentStore,
  useStepperStore,
  useCountiesStore,
  useCollegesStore,
  useSubPoolsStore,
  useEmploymentJobTitleStore,
  useAgeGroupStore,
};
